import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Users, ChevronRight, Search, Building2 } from "lucide-react";

// Selettore mailing list: scegli i condòmini (tutti o alcuni) da uno o più condomìni.
export const MailingListDialog = ({ open, onClose, buildings = [], onConfirm }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [openBuildings, setOpenBuildings] = useState(() => new Set());
  const [q, setQ] = useState("");

  useEffect(() => { if (open) { setSelected(new Set()); setQ(""); } }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return buildings;
    return buildings
      .map((b) => ({
        ...b,
        residents: b.residents.filter((r) => (r.name || "").toLowerCase().includes(term) || (r.email || "").toLowerCase().includes(term)),
      }))
      .filter((b) => b.residents.length > 0 || (b.name || "").toLowerCase().includes(term));
  }, [buildings, q]);

  const allEmails = useMemo(() => {
    const s = new Set();
    filtered.forEach((b) => b.residents.forEach((r) => s.add(r.email)));
    return s;
  }, [filtered]);

  const toggle = (email) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(email) ? n.delete(email) : n.add(email);
    return n;
  });

  const toggleBuilding = (b, checked) => setSelected((prev) => {
    const n = new Set(prev);
    b.residents.forEach((r) => (checked ? n.add(r.email) : n.delete(r.email)));
    return n;
  });

  const toggleAll = (checked) => setSelected(checked ? new Set(allEmails) : new Set());

  const buildingState = (b) => {
    const em = b.residents.map((r) => r.email);
    const sel = em.filter((e) => selected.has(e)).length;
    return sel === 0 ? false : sel === em.length ? true : "indeterminate";
  };

  const openBuild = (id) => setOpenBuildings((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const allChecked = allEmails.size > 0 && [...allEmails].every((e) => selected.has(e));

  const confirm = () => {
    if (selected.size === 0) return;
    onConfirm([...selected]);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="mailing-list-dialog" className="max-w-lg w-[95vw] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-4 w-4" />Liste condòmini</DialogTitle>
          <DialogDescription>Seleziona i destinatari dai condomìni del tuo account. Puoi sceglierli tutti o solo alcuni.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="mailing-list-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca condòmino o condominio…" className="pl-8 h-9 rounded-md" />
        </div>

        {buildings.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">Nessun condòmino con email trovato nei tuoi condomìni.</p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-xs font-medium text-gray-600 px-1">
              <Checkbox data-testid="mailing-list-select-all" checked={allChecked} onCheckedChange={toggleAll} />
              Seleziona tutti ({allEmails.size})
            </label>
            <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5">
              {filtered.map((b) => (
                <Collapsible key={b.id} open={openBuildings.has(b.id) || !!q} onOpenChange={() => openBuild(b.id)} className="border border-gray-200 rounded-md">
                  <div className="flex items-center gap-2 px-2.5 py-2">
                    <Checkbox data-testid={`mailing-list-building-${b.id}`} checked={buildingState(b)} onCheckedChange={(c) => toggleBuilding(b, !!c)} />
                    <CollapsibleTrigger className="flex items-center gap-1.5 flex-1 text-left group">
                      <ChevronRight className={`h-3.5 w-3.5 text-gray-400 transition-transform ${openBuildings.has(b.id) || q ? "rotate-90" : ""}`} />
                      <Building2 className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-sm font-medium text-gray-800">{b.name}</span>
                      <span className="text-[11px] text-gray-400">({b.residents.length})</span>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="pb-1.5 pl-9 pr-2.5 space-y-1">
                      {b.residents.map((r) => (
                        <label key={r.email} className="flex items-center gap-2 py-1 cursor-pointer">
                          <Checkbox data-testid={`mailing-list-resident-${r.email}`} checked={selected.has(r.email)} onCheckedChange={() => toggle(r.email)} />
                          <span className="flex flex-col leading-tight">
                            <span className="text-sm text-gray-700">{r.name || r.email}{r.interno ? ` · int. ${r.interno}` : ""}</span>
                            <span className="text-[11px] text-gray-400">{r.email}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="mailing-list-cancel">Annulla</Button>
          <Button onClick={confirm} disabled={selected.size === 0} className="rounded-md bg-[#007BFF] hover:bg-[#0056B3]" data-testid="mailing-list-confirm">
            Aggiungi {selected.size > 0 ? `(${selected.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
